import * as React from "react"
import { createPortal } from "react-dom"
import { cn } from "../../lib/utils"

export type TooltipSide = "top" | "bottom" | "left" | "right"

interface TooltipBox {
  readonly left: number
  readonly top: number
  readonly width: number
  readonly height: number
}

interface TooltipProps {
  children: React.ReactNode
  content: React.ReactNode
  side?: TooltipSide
  className?: string
  triggerClassName?: string
}

const TOOLTIP_GAP = 8
const OPPOSITE_SIDE: Record<TooltipSide, TooltipSide> = {
  top: "bottom",
  bottom: "top",
  left: "right",
  right: "left",
}

function unclampedPosition(side: TooltipSide, trigger: TooltipBox, tip: TooltipBox) {
  const centerX = trigger.left + trigger.width / 2 - tip.width / 2
  const centerY = trigger.top + trigger.height / 2 - tip.height / 2
  switch (side) {
    case "top":
      return { x: centerX, y: trigger.top - tip.height - TOOLTIP_GAP }
    case "bottom":
      return { x: centerX, y: trigger.top + trigger.height + TOOLTIP_GAP }
    case "left":
      return { x: trigger.left - tip.width - TOOLTIP_GAP, y: centerY }
    case "right":
      return { x: trigger.left + trigger.width + TOOLTIP_GAP, y: centerY }
  }
}

function fitsInViewport(position: { x: number; y: number }, tip: TooltipBox, viewport: { width: number; height: number }) {
  return position.x >= TOOLTIP_GAP
    && position.y >= TOOLTIP_GAP
    && position.x + tip.width <= viewport.width - TOOLTIP_GAP
    && position.y + tip.height <= viewport.height - TOOLTIP_GAP
}

export function placeTooltip(
  side: TooltipSide,
  trigger: TooltipBox,
  tip: TooltipBox,
  viewport: { width: number; height: number },
) {
  const preferred = unclampedPosition(side, trigger, tip)
  const flipped = unclampedPosition(OPPOSITE_SIDE[side], trigger, tip)
  const chosen = fitsInViewport(preferred, tip, viewport) || !fitsInViewport(flipped, tip, viewport)
    ? preferred
    : flipped
  return {
    x: Math.max(TOOLTIP_GAP, Math.min(chosen.x, viewport.width - tip.width - TOOLTIP_GAP)),
    y: Math.max(TOOLTIP_GAP, Math.min(chosen.y, viewport.height - tip.height - TOOLTIP_GAP)),
  }
}

const TooltipProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return <>{children}</>
}

const Tooltip: React.FC<TooltipProps> = ({ children, content, side = "bottom", className, triggerClassName }) => {
  const [isVisible, setIsVisible] = React.useState(false)
  const [position, setPosition] = React.useState({ x: 0, y: 0, placed: false })
  const triggerRef = React.useRef<HTMLDivElement>(null)
  const tooltipRef = React.useRef<HTMLDivElement>(null)

  const updatePosition = React.useCallback(() => {
    if (!triggerRef.current || !tooltipRef.current) return

    const triggerRect = triggerRef.current.getBoundingClientRect()
    const tooltipRect = tooltipRef.current.getBoundingClientRect()
    const next = placeTooltip(side, triggerRect, tooltipRect, {
      width: window.innerWidth,
      height: window.innerHeight,
    })
    setPosition({ ...next, placed: true })
  }, [side])

  const showTooltip = () => {
    setPosition((current) => ({ ...current, placed: false }))
    setIsVisible(true)
  }

  const hideTooltip = () => {
    setIsVisible(false)
  }

  React.useLayoutEffect(() => {
    if (!isVisible) {
      return
    }
    updatePosition()
    window.addEventListener("scroll", updatePosition, true)
    window.addEventListener("resize", updatePosition)
    return () => {
      window.removeEventListener("scroll", updatePosition, true)
      window.removeEventListener("resize", updatePosition)
    }
  }, [isVisible, content, updatePosition])

  return (
    <>
      <div
        ref={triggerRef}
        onMouseEnter={showTooltip}
        onMouseLeave={hideTooltip}
        onFocus={showTooltip}
        onBlur={hideTooltip}
        className={cn("inline-block", triggerClassName)}
      >
        {children}
      </div>
      {isVisible && createPortal(
        <div
          ref={tooltipRef}
          role="tooltip"
          className={cn(
            "pointer-events-none fixed z-[80] max-w-xs whitespace-normal break-words rounded-md border bg-slate-900 px-3 py-1.5 text-left text-sm text-white shadow-md",
            className,
          )}
          style={{
            left: position.x,
            top: position.y,
            visibility: position.placed ? "visible" : "hidden",
          }}
        >
          {content}
        </div>,
        document.body,
      )}
    </>
  )
}

const TooltipTrigger: React.FC<{ children: React.ReactNode; asChild?: boolean }> = ({ children }) => {
  return <>{children}</>
}

const TooltipContent: React.FC<{
  children: React.ReactNode
  side?: "top" | "bottom" | "left" | "right"
  className?: string
}> = ({ children }) => {
  return <>{children}</>
}

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider }
